import React, { useState, useEffect, useCallback } from 'react';
import { mapConfigs } from '../Maps/MapConfig/mapConfigs';
import AnalogStick from '../Maps/logic/AnalogStick';
import DPad from '../Maps/logic/DPad';
import { getPlayerSpriteRowOffset } from '../Maps/logic/player';
import { clamp } from '../Maps/logic/helper';
import PromoModal from '../../components/Modals/PromoModal';
import CraftModal from '../../components/Modals/CraftModal';
import EventShopModal from '../../components/Modals/EventShopModal';
import './PromoCenterMap.css';

const PromoCenterMap = () => {
  // ── Control type (analog stick or D-pad) ──
  const [controlType, setControlType] = useState(
    localStorage.getItem("controlType") || "analog"
  );

  // Listen for control type changes
  useEffect(() => {
    const handleStorageChange = () => {
      const newControlType = localStorage.getItem("controlType") || "analog";
      setControlType(newControlType);
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(handleStorageChange, 1000);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // ── movement & map ─────────────────────────────────────
  const currentFloor = "promoCenter";
  const {
    image: mapImageUrl,
    characterSprite: characterSpriteUrl,
    width: MAP_WIDTH,
    height: MAP_HEIGHT,
    collisionAreas,
    teleportZones,
    initialPlayer,
    npcs,
  } = mapConfigs[currentFloor];

  const [playerPos, setPlayerPos] = useState({ x: initialPlayer?.x || 50, y: initialPlayer?.y || 100 });
  const [direction, setDirection] = useState(initialPlayer?.direction || "down");
  const [playerStep, setPlayerStep] = useState(initialPlayer?.step || 0);
  const [showCoordinates, setShowCoordinates] = useState(true);

  // Responsive viewport function - exact same as DepartmentStoreMap
  const getResponsiveViewport = () => {
    if (window.innerWidth <= 768) {
      // Use smaller viewport for mobile to ensure proper display
      const mobileWidth = Math.min(window.innerWidth - 40, 300);
      const mobileHeight = Math.min(window.innerHeight * 0.6, 200);
      return { width: mobileWidth, height: mobileHeight };
    }
    // Use config dimensions for desktop
    return {
      width: mapConfigs[currentFloor].viewport?.width || 336,
      height: mapConfigs[currentFloor].viewport?.height || 240
    };
  };
  
  const { width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT } = getResponsiveViewport();

  const isColliding = useCallback((x, y) => {
    const rect = { x, y, width: 32, height: 32 };
    return collisionAreas.some(area =>
      rect.x < area.x + area.width &&
      rect.x + rect.width > area.x &&
      rect.y < area.y + area.height &&
      rect.y + rect.height > area.y
    );
  }, [collisionAreas]);

  const checkTeleport = useCallback((x, y) =>
    teleportZones?.find(zone =>
      x < zone.x + zone.width &&
      x + 32 > zone.x &&
      y < zone.y + zone.height &&
      y + 32 > zone.y
    ),
  [teleportZones]);

  // D-Pad handler with throttling (keyboard-style movement)
  const lastDPadMoveRef = React.useRef(0);
  const handleDPad = useCallback(({ x, y }) => {
    // Throttle D-pad input to prevent too-fast movement
    const now = Date.now();
    if (now - lastDPadMoveRef.current < 150) {
      return; // Ignore if less than 150ms since last move
    }
    
    const moving = Math.abs(x) >= 0.1 || Math.abs(y) >= 0.1;
    if (!moving) return;

    lastDPadMoveRef.current = now;

    let nx = playerPos.x;
    let ny = playerPos.y;
    let dir = direction;

    // Move by 4 pixels like keyboard
    if (Math.abs(x) > Math.abs(y)) {
      dir = x > 0 ? "right" : "left";
      nx += (x > 0 ? 1 : -1) * 4;
    } else {
      dir = y > 0 ? "down" : "up";
      ny += (y > 0 ? 1 : -1) * 4;
    }

    if (!isColliding(nx, ny)) {
      const tp = checkTeleport(nx, ny);
      if (tp) {
        console.log(`Teleporting to ${tp.targetFloor} at (${tp.targetX}, ${tp.targetY})`);
        return;
      }
      setPlayerPos({ x: nx, y: ny });
      setDirection(dir);
      setPlayerStep(prev => (prev + 1) % 3);
    }
  }, [playerPos, direction, isColliding, checkTeleport]);

  // Analog stick handler (smooth movement)
  const handleStick = useCallback(({ x, y }) => {
    const TH = 0.1, speed = 1;
    if (Math.abs(x) < TH && Math.abs(y) < TH) return;
    const nx = playerPos.x + x * speed;
    const ny = playerPos.y + y * speed;
    const dir = Math.abs(x) > Math.abs(y)
      ? (x > 0 ? "right" : "left")
      : (y > 0 ? "down" : "up");
    if (!isColliding(nx, ny)) {
      const tp = checkTeleport(nx, ny);
      if (tp) {
        console.log(`Teleporting to ${tp.targetFloor} at (${tp.targetX}, ${tp.targetY})`);
        return;
      }
      setPlayerPos({ x: nx, y: ny });
      setDirection(dir);
      setPlayerStep(prev => (prev + 1) % 3);
    }
  }, [playerPos, isColliding, checkTeleport]);

  useEffect(() => {
    const keyMap = {
      ArrowUp: [0, -4, "up"],    w: [0,-4,"up"],
      ArrowDown: [0, 4, "down"], s: [0,4,"down"],
      ArrowLeft: [-4,0,"left"],   a: [-4,0,"left"],
      ArrowRight: [4,0,"right"],  d: [4,0,"right"],
    };
    const onKeyDown = e => {
      // Toggle coordinates display with C key
      if (e.key === 'c') {
        setShowCoordinates(prev => !prev);
        return;
      }
      
      const k = keyMap[e.key];
      if (!k) return;
      e.preventDefault();
      const [dx, dy, nd] = k;
      const nx = playerPos.x + dx, ny = playerPos.y + dy;
      if (!isColliding(nx, ny)) {
        const tp = checkTeleport(nx, ny);
        if (tp) {
          console.log(`Teleporting to ${tp.targetFloor} at (${tp.targetX}, ${tp.targetY})`);
          return;
        }
        setPlayerPos({ x: nx, y: ny });
        setDirection(nd);
        if (e.repeat) setPlayerStep(prev => (prev + 1) % 3);
        else [1,2,0].forEach((s,i)=>
          setTimeout(() => setPlayerStep(s), i*150)
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [playerPos, isColliding, checkTeleport]);

  // ── floating text system ─────────────────────────────────
  const [floatingTexts, setFloatingTexts] = useState([]);

  const spawnText = (text, x, y, color = "#ffe066", imageUrl = null) => {
    const id = Date.now() + Math.random();
    setFloatingTexts((ft) => [
      ...ft,
      { id, text, x, y, color, imageUrl, created: Date.now() },
    ]);
    setTimeout(() => {
      setFloatingTexts((ft) => ft.filter((f) => f.id !== id));
    }, 2500);
  };

  // ── modal states ─────────────────────────────────────
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [craftModalOpen, setCraftModalOpen] = useState(false);
  const [eventShopOpen, setEventShopOpen] = useState(false);
  const [eventShopId, setEventShopId] = useState(1); // Default to Magma Girl's shop ID

  // ── camera system ─────────────────────────────────────────
  // Calculate camera position to follow player - exact same as GameMap
  const camX = clamp(playerPos.x - VIEWPORT_WIDTH / 2, 0, MAP_WIDTH - VIEWPORT_WIDTH);
  const camY = clamp(playerPos.y - VIEWPORT_HEIGHT / 2, 0, MAP_HEIGHT - VIEWPORT_HEIGHT);

  // ── NPC interaction ───────────────────────────────────────
  const handleNPCClick = (npc) => {
    console.log(`Clicked NPC: ${npc.id}, type: ${npc.type}`);
    
    if (npc.type === 'promo') {
      setPromoModalOpen(true);
    } else if (npc.type === 'craft') {
      setCraftModalOpen(true);
    } else if (npc.type === 'event') {
      // For Magma Girl or other event NPCs
      setEventShopOpen(true);
      // You can set different shop IDs based on the NPC if needed
      if (npc.id === 'Magma-Girl') {
        setEventShopId(1); // Magma Girl's shop ID
      }
    }
  };

  return (
    <div className="layout-container">
      <div className="map-section">
        <div className="map-wrapper">
          <div className="map-scale-wrapper">
            <div
              className="game-container"
              style={{
                width: VIEWPORT_WIDTH,
                height: VIEWPORT_HEIGHT,
                overflow: 'visible'
              }}
            >
             
              <div
                className="map"
                style={{
                  backgroundImage: `url(${mapImageUrl})`,
                  backgroundPosition: `-${camX}px -${camY}px`,
                  width: VIEWPORT_WIDTH,
                  height: VIEWPORT_HEIGHT,
                  backgroundSize: `${MAP_WIDTH}px ${MAP_HEIGHT}px`,
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: '#000'
                }}
              >
                <div
                  className="character"
                  style={{
                    left: playerPos.x - camX,
                    top: playerPos.y - camY,
                    backgroundImage: `url(${characterSpriteUrl})`,
                    backgroundPosition: `-${playerStep*32}px -${getPlayerSpriteRowOffset(direction)}px`
                  }}
                />
                
                {/* Floating text overlay */}
                {floatingTexts.map((ft) => (
                  <div
                    key={ft.id}
                    className="floating-text"
                    style={{
                      position: "absolute",
                      left: ft.x - camX,
                      top: ft.y - camY,
                      color: ft.color,
                      fontSize: "12px",
                      fontWeight: "bold",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
                      pointerEvents: "none",
                      zIndex: 1000,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      animation: "floatUp 2.5s ease-out forwards"
                    }}
                  >
                    {ft.imageUrl && (
                      <img
                        src={ft.imageUrl}
                        alt=""
                        style={{
                          width: "16px",
                          height: "16px",
                          imageRendering: "pixelated"
                        }}
                      />
                    )}
                    {ft.text}
                  </div>
                ))}
                
                {/* NPCs */}
                {npcs && npcs.map(npc=>(
                  <div
                    key={npc.id}
                    className="trainer"
                    onClick={() => handleNPCClick(npc)}
                    style={{
                      position:'absolute',
                      left: npc.x - camX,
                      top:  npc.y - camY,
                      width: npc.width,
                      height: npc.height,
                      backgroundImage: `url(${npc.sprite})`,
                      backgroundPosition: `-${npc.step * 32}px -${npc.direction * 32}px`,
                      cursor:'pointer',
                      zIndex: 100
                    }}
                  />
                ))}
                {/* Promo Modal
                {collisionAreas && collisionAreas.map((area, index) => (
                  <div
                    key={`collision-${index}`}
                    className="collision-area"
                    style={{
                      position: 'absolute',
                      left: area.x - camX,
                      top: area.y - camY,
                      width: area.width,
                      height: area.height,
                    }}
                  />
                ))}
         */}
              </div>

           
              <div className="analog-stick-wrapper-base">
                {controlType === 'analog' ? (
                  <AnalogStick onMove={handleStick}/>
                ) : (
                  <DPad onMove={handleDPad}/>
                )}
              </div>
            </div>
          </div>

          {/* Promo Modal */}
          <PromoModal
            isOpen={promoModalOpen}
            toggle={() => setPromoModalOpen(false)}
            spawnText={spawnText}
            playerPos={playerPos}
          />

          {/* Craft Modal */}
          <CraftModal
            isOpen={craftModalOpen}
            toggle={() => setCraftModalOpen(false)}
            spawnText={spawnText}
            playerPos={playerPos}
          />

          {/* Event Shop Modal */}
          <EventShopModal
            isOpen={eventShopOpen}
            toggle={() => setEventShopOpen(false)}
            shopId={eventShopId}
            playerPos={playerPos}
            spawnText={spawnText}
          />

        </div>
      </div>
    </div>
  );
};

export default PromoCenterMap;
